import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  BookOpen, Award, Clock, Target, ArrowRight, Gift, 
  Copy, Users, DollarSign, Share2, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, statsRes, referralRes] = await Promise.all([
        axios.get(`${API}/purchases/me`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/student/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/referral/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setEnrolledCourses(coursesRes.data);
      setStats(statsRes.data);
      setReferralStats(referralRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = `${window.location.origin}/register?ref=${referralStats?.referral_code || ''}`;

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on AISkillFlow!',
          text: `Sign up for AISkillFlow and get ${referralStats?.discount_percent}% off your first subscription!`,
          url: referralLink
        });
      } catch (error) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  const statCards = [
    {
      icon: BookOpen,
      label: 'Purchased Courses',
      value: enrolledCourses.length || 0,
      color: 'text-primary bg-primary/10'
    },
    {
      icon: Award,
      label: 'Completed',
      value: stats?.completed_courses || 0,
      color: 'text-green-600 bg-green-100'
    },
    {
      icon: Target,
      label: 'Avg. Progress',
      value: `${Math.round(stats?.average_progress || 0)}%`,
      color: 'text-amber-600 bg-amber-100'
    },
    {
      icon: Clock,
      label: 'Lifetime Access',
      value: 'Active',
      color: 'text-green-600 bg-green-100'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="student-dashboard">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-slate-900">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mt-1">Continue your learning journey</p>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((stat, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                      <p className="text-sm text-slate-600">{stat.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Referral Program Section */}
          <Card className="mb-8 border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-secondary/20 to-lime-100 p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                  <Gift className="w-6 h-6 text-secondary-foreground" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-slate-900">
                    Refer Friends, Earn Rewards
                  </h2>
                  <p className="text-slate-600">
                    Get ${referralStats?.reward_per_referral || 10} credit for each friend who subscribes
                  </p>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Referral Link */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Your Referral Link
                    </label>
                    <div className="flex gap-2">
                      <Input 
                        value={referralLink} 
                        readOnly 
                        className="font-mono text-sm bg-slate-50"
                        data-testid="referral-link"
                      />
                      <Button 
                        variant="outline" 
                        onClick={copyReferralLink}
                        data-testid="copy-referral-btn"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Your Referral Code
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="px-4 py-2 bg-slate-100 rounded-lg font-mono text-lg font-bold text-primary">
                        {referralStats?.referral_code || '---'}
                      </code>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
                    onClick={shareReferral}
                    data-testid="share-referral-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share with Friends
                  </Button>
                </div>

                {/* Referral Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <Users className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">
                      {referralStats?.total_referrals || 0}
                    </p>
                    <p className="text-sm text-slate-600">Total Referrals</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <Award className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">
                      {referralStats?.converted_referrals || 0}
                    </p>
                    <p className="text-sm text-slate-600">Converted</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">
                      {referralStats?.pending_referrals || 0}
                    </p>
                    <p className="text-sm text-slate-600">Pending</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-600">
                      ${(referralStats?.referral_credits || 0).toFixed(2)}
                    </p>
                    <p className="text-sm text-slate-600">Credits Earned</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Enrolled Courses */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold text-slate-900">My Courses</h2>
              <Button variant="outline" onClick={() => navigate('/courses')} data-testid="browse-more-btn">
                Browse More
              </Button>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-slate-200 aspect-video rounded-t-xl" />
                    <div className="p-5 space-y-3 bg-white rounded-b-xl border border-t-0 border-slate-200">
                      <div className="h-4 bg-slate-200 rounded w-1/3" />
                      <div className="h-6 bg-slate-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : enrolledCourses.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {enrolledCourses.map((course) => (
                  <Card key={course.id} className="overflow-hidden border-slate-200">
                    <div className="aspect-video relative">
                      <img 
                        src={course.thumbnail_url} 
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-200">
                        <div 
                          className="h-full bg-secondary transition-all duration-300"
                          style={{ width: `${course.progress?.progress_percent || 0}%` }}
                        />
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 line-clamp-1">
                        {course.title}
                      </h3>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-slate-600">
                          {Math.round(course.progress?.progress_percent || 0)}% complete
                        </span>
                        <span className="text-sm text-slate-600">
                          {course.progress?.completed_lessons?.length || 0}/{course.lessons?.length || 0} lessons
                        </span>
                      </div>
                      <Progress value={course.progress?.progress_percent || 0} className="h-2 mb-4" />
                      <Button 
                        className="w-full"
                        onClick={() => navigate(`/learn/${course.id}`)}
                        data-testid={`continue-course-${course.id}`}
                      >
                        Continue Learning
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No courses yet</h3>
                <p className="text-slate-600 mb-6">Start learning by enrolling in your first course</p>
                <Button onClick={() => navigate('/courses')} data-testid="explore-courses-btn">
                  Explore Courses
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
